'use-client';

import {
    Button,
    ButtonDropdown,
    LayoutContainer,
    Toolbar,
} from '@petsmart-ui/sparky';
import { useEffect, useMemo, useState } from 'react';

import FilterByRating from '@app/plp/components/FilterByRating/FilterByRating';
import '@app/plp/components/FilterContainer/FilterContainer.scss';
import FilterRefinementList from '@app/plp/components/FilterRefinementList';
import FindStoreNearMe from '@app/plp/components/FindStoreNearMe';
import PriceRange from '@app/plp/components/PriceRange';
import SortProduct from '@app/plp/components/SortProduct/SortProduct';
import {
    DEVICE_TYPE,
    clsx,
    useDeviceType,
    usePageLayout,
} from '@libs/common-next';
import {
    ClearRefinements,
    useClearRefinements,
    useCurrentRefinements,
    useInstantSearch,
    useRange,
    useRefinementList,
    useSortBy,
} from 'react-instantsearch';

import { CATEGORIES_ATTRIBUTE } from '@app/plp/components/SearchLayout/utils';
import LoadingOverlay from '@app/plp/components/LoadingOverlay/LoadingOverlay';
import { usePriceRangeContext } from '@app/plp/providers/PriceRangeProvider';
import {
    AlgoliaIndice,
    FILTERS,
    SORT_INDEX_PLP,
    SORT_INDEX_SEARCH,
    countFilters,
    getRangeTitle,
    getSortLabelFromIndex,
    totalFilters,
    useFeaturedBrand,
    useSearchPage,
} from '@libs/algolia';
import dynamic from 'next/dynamic';
import { uniq } from 'lodash';

const DrawerFilter = dynamic(() => import('@app/plp/components/DrawerFilter'), {
    ssr: true,
    loading: () => <LoadingOverlay show />,
});

const handlerSize: Record<string, object> = {
    mobile: {
        behavior: 'overflow-sm',
        className: 'filter-wrapper filter-wrapper--mobile',
    },
    tablet: {
        className: 'filter-wrapper filter-wrapper--tablet',
    },
    desktop: {
        className: 'filter-wrapper filter-wrapper--desktop',
    },
};

type FilterContainerProps = {
    pageId: string;
    maxRateAvailable: number;
};

export default function FilterContainer({
    pageId,
    maxRateAvailable,
}: FilterContainerProps) {
    const isFeatured = useFeaturedBrand();
    const { storeDrawerOpen, setStoreDrawerOpen } = usePageLayout();
    const isSearchPage = useSearchPage();
    const sortby = useSortBy(isSearchPage ? SORT_INDEX_SEARCH : SORT_INDEX_PLP);

    const { minValue, maxValue, clearRange } = usePriceRangeContext();
    const { refine: clearShop } = useClearRefinements({
        includedAttributes: [FILTERS.SHOP_BY_STORE.ATTRIBUTE],
    });

    const priceRange = useRange({ attribute: FILTERS.RANGE.ATTRIBUTE });
    const excludedAttributes = [CATEGORIES_ATTRIBUTE, 'query'];
    if (isFeatured) {
        excludedAttributes.push(FILTERS.BRAND.ATTRIBUTE);
    }
    const { items } = useCurrentRefinements({
        excludedAttributes,
    });

    const [isActive, setIsActive] = useState<boolean>(false);
    const [orderLabel, setOrderLabel] = useState<string>(
        getSortLabelFromIndex(sortby.currentRefinement) ?? ''
    );
    const [isStoreFilterApplied, setIsStoreFilterApplied] =
        useState<boolean>(false);
    const deviceSize = useDeviceType();
    const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);

    const handleDrawer = () => {
        setIsActive(!isActive);
    };

    function handleBrandDropdownState(value: boolean): void {
        setIsBrandDropdownOpen(value);
    }

    const total = totalFilters(items);
    const counters = countFilters(items);

    const counterFilterTitle = () => (total > 0 ? `(${total})` : '');

    const isMobile = () =>
        [DEVICE_TYPE.MOBILE, DEVICE_TYPE.TABLET].includes(
            deviceSize as DEVICE_TYPE
        );

    const behaviorResponsive = !isMobile() ? undefined : 'responsive';

    const buttonDropdownClasses = clsx(
        'filter-container__button-dropdown list-filter',
        Boolean(total) && 'all-filters'
    );

    const sortProducts = () => {
        sortby.refine(
            isSearchPage
                ? AlgoliaIndice.PRODUCTS
                : AlgoliaIndice.PRODUCTS_BEST_SELLERS
        );
    };

    const dynamicFacets = uniq(
        useInstantSearch()?.results?.renderingContent?.facetOrdering?.facets
            ?.order ?? []
    );

    const useCountItems = (attribute: string) =>
        useRefinementList({ attribute })?.items?.length;

    useEffect(() => {
        setOrderLabel(
            getSortLabelFromIndex(sortby.currentRefinement) ?? orderLabel
        );
    }, [orderLabel, sortby.currentRefinement]);

    const hasStoreFilter = items.some(
        item => item.attribute === FILTERS.SHOP_BY_STORE.ATTRIBUTE
    );
    const hasBrandFilter = items.some(
        item => item.attribute === FILTERS.BRAND.ATTRIBUTE
    );
    const hasRangeFilter = items.some(
        item => item.attribute === FILTERS.RANGE.ATTRIBUTE
    );
    const hasRatingFilter = items.some(
        item => item.attribute === FILTERS.RATING.ATTRIBUTE
    );

    const clearFiltersButtonClassName = 'drawer-filter__button-clear-filters';
    const [isDrawerCreated, setIsDrawerCreated] = useState(false);
    const renderDrawer = useMemo(() => {
        if (isDrawerCreated || (isActive && !storeDrawerOpen)) {
            setIsDrawerCreated(true);
            return (
                <DrawerFilter
                    pageId={pageId}
                    clearShop={clearShop}
                    isActive={isActive && !storeDrawerOpen}
                    isMobileVersion={isMobile()}
                    handlerDrawer={handleDrawer}
                    label={FILTERS.DRAWER.LABEL}
                    description={FILTERS.DRAWER.DESCRIPTION}
                    sort={sortby}
                    priceRange={priceRange}
                    openStoreModal={() =>
                        setStoreDrawerOpen({ pageType: 'plp' })
                    }
                    dynamicFacets={dynamicFacets}
                    countItems={useCountItems}
                    sortProducts={sortProducts}
                    maxRateAvailable={maxRateAvailable}
                    refinements={[]}
                    className="filter-container__drawer-filter"
                />
            );
        }
        return null;
    }, [isActive, storeDrawerOpen, items]);

    return (
        <LayoutContainer {...handlerSize[deviceSize ?? DEVICE_TYPE.MOBILE]}>
            <div className="filter-container" data-testid="filter-container">
                <Toolbar>
                    <Toolbar.Item>
                        <Button
                            variant="toolbar"
                            iconName="filter"
                            iconSize="sm"
                            onClick={handleDrawer}
                            data-testid="all-filters-button"
                            text={`${
                                isMobile()
                                    ? FILTERS.HEADER_TITLE.MOBILE
                                    : FILTERS.HEADER_TITLE.DESKTOP
                            } ${counterFilterTitle()}`}
                            className={buttonDropdownClasses}
                        />
                    </Toolbar.Item>
                    <Toolbar.Item>
                        <ButtonDropdown
                            behavior={behaviorResponsive}
                            variant="toolbar"
                            buttonText={FILTERS.SHOP_BY_STORE.TITLE}
                            ariaLabelledBy={FILTERS.SHOP_BY_STORE.TITLE}
                            drawerTitle={FILTERS.SHOP_BY_STORE.TITLE}
                            className={`filter-container__button-dropdown store-filter ${
                                isStoreFilterApplied ? 'all-filters' : ''
                            }`}
                            onChange={() => null}
                            data-testid="filter-button-pickup-delivery"
                            titleAfter={
                                hasStoreFilter && (
                                    <ClearRefinements
                                        includedAttributes={[
                                            FILTERS.SHOP_BY_STORE.ATTRIBUTE,
                                        ]}
                                        title={FILTERS.SHOP_BY_STORE.TITLE}
                                        translations={{
                                            resetButtonText:
                                                FILTERS.CLEAR.TITLE,
                                        }}
                                        classNames={{
                                            button: clearFiltersButtonClassName,
                                        }}
                                    />
                                )
                            }
                        >
                            <div className="filter-drawer-container">
                                <FindStoreNearMe
                                    pageId={pageId}
                                    openStoreModal={() =>
                                        setStoreDrawerOpen({ pageType: 'plp' })
                                    }
                                    clearShop={clearShop}
                                    showActions
                                    handleIsStoreFilterApplied={
                                        setIsStoreFilterApplied
                                    }
                                />
                            </div>
                        </ButtonDropdown>
                    </Toolbar.Item>
                    {!isFeatured && (
                        <Toolbar.Item>
                            <ButtonDropdown
                                behavior={behaviorResponsive}
                                variant="toolbar"
                                buttonText={`${FILTERS.BRAND.TITLE} ${counters(
                                    FILTERS.BRAND.ATTRIBUTE
                                )}`}
                                ariaLabelledBy={FILTERS.BRAND.TITLE}
                                drawerTitle={FILTERS.BRAND.TITLE}
                                className="filter-container__button-dropdown brand-filter"
                                data-testid="filter-refinement-button-brand"
                                onChange={() => null}
                                titleAfter={
                                    hasBrandFilter && (
                                        <ClearRefinements
                                            includedAttributes={[
                                                FILTERS.BRAND.ATTRIBUTE,
                                            ]}
                                            title={FILTERS.CLEAR.TITLE}
                                            translations={{
                                                resetButtonText:
                                                    FILTERS.CLEAR.TITLE,
                                            }}
                                            classNames={{
                                                button: clearFiltersButtonClassName,
                                            }}
                                            data-testid="filter-refinement-list-brand-button-clear"
                                        />
                                    )
                                }
                                // eslint-disable-next-line react/jsx-no-bind -- We need to pass function as prop to see the active state of the dropdown
                                onStateChange={handleBrandDropdownState}
                            >
                                <div className="filter-drawer-container">
                                    <FilterRefinementList
                                        pageId={pageId}
                                        attribute={FILTERS.BRAND.ATTRIBUTE}
                                        showActions
                                        limit={FILTERS.BRAND.SHOW_MORE_LIMIT}
                                        placeholder={FILTERS.BRAND.PLACEHOLDER}
                                        isDropdownOpen={isBrandDropdownOpen}
                                    />
                                </div>
                            </ButtonDropdown>
                        </Toolbar.Item>
                    )}
                    <Toolbar.Item>
                        <ButtonDropdown
                            behavior={behaviorResponsive}
                            variant="toolbar"
                            buttonText={getRangeTitle(minValue, maxValue)}
                            ariaLabelledBy={FILTERS.RANGE.TITLE}
                            drawerTitle={FILTERS.RANGE.TITLE}
                            className="filter-container__button-dropdown filter-range"
                            data-testid="filter-by-price"
                            onChange={() => null}
                            titleAfter={
                                hasRangeFilter && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        text={FILTERS.CLEAR.TITLE}
                                        onClick={() => clearRange()}
                                        className={clearFiltersButtonClassName}
                                    />
                                )
                            }
                        >
                            <div className="filter-drawer-container price-tool-bar">
                                <PriceRange
                                    pageId={pageId}
                                    priceRange={priceRange}
                                    showClear
                                    showView
                                />
                            </div>
                        </ButtonDropdown>
                    </Toolbar.Item>
                    <Toolbar.Item>
                        <ButtonDropdown
                            behavior={behaviorResponsive}
                            variant="toolbar"
                            buttonText={counters(
                                FILTERS.RATING.ATTRIBUTE,
                                FILTERS.RATING.TITLE
                            )}
                            ariaLabelledBy={FILTERS.RATING.TITLE}
                            drawerTitle={FILTERS.RATING.TITLE}
                            className="filter-container__button-dropdown filter-rating"
                            data-testid="filter-button-rating"
                            onChange={() => null}
                            titleAfter={
                                hasRatingFilter && (
                                    <ClearRefinements
                                        includedAttributes={[
                                            FILTERS.RATING.ATTRIBUTE,
                                        ]}
                                        title={FILTERS.CLEAR.TITLE}
                                        translations={{
                                            resetButtonText:
                                                FILTERS.CLEAR.TITLE,
                                        }}
                                        classNames={{
                                            button: clearFiltersButtonClassName,
                                        }}
                                        data-testid="filter-refinement-list-rating-button-clear"
                                    />
                                )
                            }
                        >
                            <div className="filter-drawer-container">
                                <FilterByRating
                                    pageId={pageId}
                                    attribute={FILTERS.RATING.ATTRIBUTE}
                                    items={FILTERS.RATING.ITEMS}
                                    showActions
                                />
                            </div>
                        </ButtonDropdown>
                    </Toolbar.Item>
                    {isMobile() ? null : (
                        <Toolbar.Item align="right">
                            <ButtonDropdown
                                behavior={behaviorResponsive}
                                variant="toolbar"
                                buttonText={
                                    <>
                                        {FILTERS.SORT_BY.TITLE}
                                        <span className="selected-text">
                                            {` ${orderLabel}`}
                                        </span>
                                    </>
                                }
                                ariaLabelledBy={FILTERS.SORT_BY.TITLE}
                                data-testid="filter-button-sort-by"
                                className="filter-container__sorting-product sort-filter"
                                onChange={() => null}
                            >
                                <SortProduct type="check" {...sortby} />
                            </ButtonDropdown>
                        </Toolbar.Item>
                    )}
                </Toolbar>
            </div>
            {renderDrawer}
        </LayoutContainer>
    );
}
